import React, { useEffect, useState, useContext, useRef } from 'react';
import { AppContext } from "../../util/AppContext";
import { getConfigValue } from "../../util/getConfigValue";
import { useErrorBoundary } from "react-error-boundary";
import { getUtilizationReportTotalLoginsByTime } from '../../API/UtilizationReportingDashboard'
import PropType from 'prop-types';
import Tooltip from '../Tooltip';

RepUtilizationTotalLoginsCard.propTypes = {
    filters: PropType.object
};
function RepUtilizationTotalLoginsCard({ filters }) {
    const [appState] = useContext(AppContext);
    const [totalLoginData, setTotalLoginData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const { showBoundary } = useErrorBoundary();
    const totalLoginsTooltipTarget = useRef(null);
    const totalUniqueLoginsTooltipTarget = useRef(null);
    const [showTotalLoginsTooltip, setShowTotalLoginsTooltip] = useState(false);
    const [showTotalUniqueLoginsTooltip, setShowTotalUniqueLoginsTooltip] = useState(false);
    const chartEnabled = JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.RepUtilizationTotalLoginsCard?.Enabled, appState)?.Value === 'true') || false;

    function constructTotalLoginData(logins) {
        function calculateTotalLoginData(current, prior) {
            return (prior === 0 ? null : ((current - prior) / prior) * 100);
        }
        var data = {
            currentPeriod: {
                total: {
                    label: getConfigValue(appState?.config?.UtilizationDashboard?.TotalLoginsCard?.TotalLogins?.Label, appState)?.Value || null,
                    count: logins.currentPeriod.dataSets[0].data[0],
                    change: {
                        hasPriorData: logins.priorPeriod.dataSets[0].data[0] !== 0,
                        percent: calculateTotalLoginData(logins.currentPeriod.dataSets[0].data[0], logins.priorPeriod.dataSets[0].data[0])
                    }
                },
                unique: {
                    label: getConfigValue(appState?.config?.UtilizationDashboard?.TotalLoginsCard?.TotalUniqueLogins?.Label, appState)?.Value || null,
                    count: logins.currentPeriod.dataSets[0].data[1],
                    change: {
                        hasPriorData: logins.priorPeriod.dataSets[0].data[1] !== 0,
                        percent: calculateTotalLoginData(logins.currentPeriod.dataSets[0].data[1], logins.priorPeriod.dataSets[0].data[1])
                    }
                }
            }
        };

        return data;
    }


    const getTotalLogins = async (selectedTimePeriod) => {
        setIsLoading(true);

        await getUtilizationReportTotalLoginsByTime(selectedTimePeriod)
            .then((responseJson) => {
                setTotalLoginData(constructTotalLoginData(responseJson));
                setIsLoading(false);
            })
            .catch((err) => {
                showBoundary(err);
            });
    }


    useEffect(() => {
        if (chartEnabled && filters?.timePeriodId !== null) {
            getTotalLogins(filters.timePeriodId);
        }
    }, [filters.timePeriodId]);


    // UI Helpers    
    const setTotalLoginsClass = (percent, currentCount) => {
        if ((percent === null || percent === 0)) {
            return 'nutrualChange';
        }
        return percent > 0 || (percent == null && currentCount > 0) ? 'positiveChange' : 'negativeChange';
    }
    const formatPercent = (percent) => {
        if (percent === null || percent === 0) {
            return '';
        }
        return percent.toFixed(1) + '%';
    }
    const formatTotalCount = (count) => {
        if (count > 1000) {
            return (count / 1000).toFixed(1) + 'K';
        }
        return count;
    }
    const setTotalCountChangeIcon = (percent, currentCount) => {
        if ((percent === null || percent === 0)) {
            return '';
        }

        return percent > 0 || (percent == null && currentCount > 0) ? "fas fa-caret-up" : "fas fa-caret-down";
    }

    const handleTooltipEvent = (action, ref) => {
        let set;
        let value;

        if(ref === totalLoginsTooltipTarget) {
            set = setShowTotalLoginsTooltip;
            value = showTotalLoginsTooltip;
        } else if (ref === totalUniqueLoginsTooltipTarget) {
            set = setShowTotalUniqueLoginsTooltip;
            value = showTotalUniqueLoginsTooltip;
        } else {
            return;
        }

        switch (action) {
            case 'show':
                set(true);
                break;
            case 'hide':
                set(false);
                break;
            case 'toggle':
                set(!value)
                break;
            default:
                break;
        }
    }

    const valueToolTipWidget = (lableText, messageText, showTooltip, tooltipRef) => {
        return (
            <>
                <button className='btn-ghost' 
                    ref={tooltipRef}
                    type='button'
                    onMouseEnter={() => handleTooltipEvent('show', tooltipRef)}
                    onMouseLeave={() => handleTooltipEvent('hide', tooltipRef)}
                    onClick={() => handleTooltipEvent('toggle', tooltipRef)}><span className='nutrualChange'>{lableText}</span></button>
                <Tooltip
                    text={messageText}
                    placement='right'
                    ref={tooltipRef}
                    showTooltip={showTooltip} />
            </>
        )
    }

    const valueDisplayWidget = (data, config, showTooltip, tooltipRef) => {
        return (
            <>
                <span className="count">{formatTotalCount(data.count)}</span>
                <div className={`d-flex align-items-center fw-bold ${setTotalLoginsClass(data.change.percent, data.count)}`}>
                    <span className={setTotalCountChangeIcon(data.change.percent)}></span>
                    {formatPercent(data.change.percent)}
                    {!data.change.hasPriorData ? (
                        valueToolTipWidget(
                            getConfigValue(config?.NoPriorData?.Label, appState)?.Value || "*",
                            getConfigValue(config?.NoPriorData?.ToolTip, appState)?.Value || "N/A because the previous time period had no logins.",
                            showTooltip,
                            tooltipRef
                        )
                    ) : (data.change.percent === 0 && (
                        valueToolTipWidget(
                            getConfigValue(config?.NoChange?.Label, appState)?.Value || "N/A",
                            getConfigValue(config?.NoChange?.ToolTip, appState)?.Value || "This value is equivalent to the value from the previous time.",
                            showTooltip,
                            tooltipRef
                        )
                    ))}
                </div>
            </>
        );
    }

    return (
        <>
            {chartEnabled &&
                <div className="card pd-card pt-4 pt-md-4 pb-4 pb-md-6 chartCardContainer">
                    <div className="row justify-content-center">
                        {isLoading ? (
                            <div className="col-1 col-md-1 mb-0 mb-md-0">
                                <p>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</p>
                            </div>
                        ) : (
                            <>
                                <div className="col-10 col-md-5 mb-6 mb-md-0 d-flex align-items-center">
                                    <span className="label">{getConfigValue(
                                        appState?.config?.UtilizationDashboard?.TotalLoginsCard?.TotalLogins?.Label,
                                        appState
                                    )?.Value || null}</span>
                                    {valueDisplayWidget(
                                        totalLoginData.currentPeriod.total, 
                                        appState?.config?.UtilizationDashboard?.Charts?.RepUtilizationTotalLoginsCard?.TotalLogins, 
                                        showTotalLoginsTooltip, 
                                        totalLoginsTooltipTarget)}
                                </div>

                                <div className="col-10 col-md-5 d-flex align-items-center">
                                    <span className="label">{getConfigValue(
                                        appState?.config?.UtilizationDashboard?.TotalLoginsCard?.TotalUniqueLogins?.Label,
                                        appState
                                    )?.Value || null}</span>
                                    {valueDisplayWidget(
                                        totalLoginData.currentPeriod.unique, 
                                        appState?.config?.UtilizationDashboard?.Charts?.RepUtilizationTotalLoginsCard?.TotalUniqueLogins, 
                                        showTotalUniqueLoginsTooltip, 
                                        totalUniqueLoginsTooltipTarget)}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            }
        </>
    );
}

export default RepUtilizationTotalLoginsCard;
